import type { BoundObjectType } from '@generated/graphql';
import type { Strategy } from '@interfaces/financialProduct';
import type { TranslatedString } from '@interfaces/shared';

/**
 * Local mirrors of product-service's model change proposal API. Codegen has the
 * proposal inputs but not yet `organizationId` on create (OVT-7783), so these
 * stay hand-written until the schema catches up. Keep in sync with
 * product-service: src/api/org/typeDefs/mutation.ts and
 * src/api/org/typeDefs/types/modelChangeProposal.ts.
 */

/** A holding's weight in the model, as a proposal carries it. */
export type ModelChangeAllocationInput = {
  financialProductId: string;
  percentage: number;
  isCashEquivalent?: boolean;
  assetClassId?: string;
};

/** A group's weight, on asset-class-strategy models. */
export type ModelChangeAssetClassBranchInput = {
  assetClassId: string;
  targetPercentage: number;
};

/**
 * The IPS as a proposal names it. The same statement is called
 * `translatedPortfolioDescription` and friends on the model portfolio itself,
 * so the two shapes cannot be passed to each other unmapped.
 */
export type ModelChangeModelDetailsInput = {
  name?: TranslatedString;
  portfolioDescription?: TranslatedString;
  investmentObjective?: TranslatedString;
  liquidity?: TranslatedString;
  portfolioDisclaimer?: TranslatedString;
  themeName?: TranslatedString;
};

/** An asset class's allowed band, the shape `setupEntityBoundModel` also takes. */
export type TargetAllocationInput = {
  secondaryAssetClassId: string;
  min: number;
  max: number;
};

export type CreateModelChangeProposalInput = {
  /**
   * The shelf model being changed, never the household's own copy of it:
   * product-service mints or reuses the copy itself and refuses a clone here.
   */
  financialProductId: string;
  objectType: BoundObjectType.Household;
  objectId: string;
  /**
   * Owning organization of the bound entity (`objectId`), not the advisor's
   * selected / active org. product-service scopes the clone and proposal to it.
   */
  organizationId: string;
  /**
   * Required by the schema even for a statement-only change, where it mirrors
   * the weights the model already has.
   */
  proposedAllocations: ModelChangeAllocationInput[];
  currentAllocations?: ModelChangeAllocationInput[];
  proposedModelDetails?: ModelChangeModelDetailsInput;
  currentModelDetails?: ModelChangeModelDetailsInput;
  proposedAssetClassThresholds?: TargetAllocationInput[];
  currentAssetClassThresholds?: TargetAllocationInput[];
  proposedAssetClassBranches?: ModelChangeAssetClassBranchInput[];
  currentAssetClassBranches?: ModelChangeAssetClassBranchInput[];
};

/** Only accepted while the proposal is still a draft. */
export type UpdateModelChangeProposalInput = Partial<
  Omit<
    CreateModelChangeProposalInput,
    'financialProductId' | 'objectType' | 'objectId' | 'organizationId'
  >
> & { modelChangeProposalId: string };

/**
 * What the app can ask of a proposal. `sign` is left to product-service, which
 * signs in response to the workflow's transition action rather than to anything
 * the app calls directly.
 */
export type ModelChangeProposalTransition = 'submit' | 'cancel';

export type TransitionModelChangeProposalInput = {
  modelChangeProposalId: string;
  transition: ModelChangeProposalTransition;
};

export type ModelChangeProposalState =
  | 'DRAFT'
  | 'PENDING_SIGNATURE'
  | 'SIGNED'
  | 'COMPLETED'
  | 'CANCELED';

/**
 * The states a household can only be in one of at a time: product-service
 * refuses a second proposal while one of these is open, so a workflow that
 * comes back to the allocations step has to find and reuse it.
 */
export const ACTIVE_PROPOSAL_STATES: ModelChangeProposalState[] = [
  'DRAFT',
  'PENDING_SIGNATURE',
  'SIGNED',
];

export type ModelChangeAllocation = ModelChangeAllocationInput;

export type ModelChangeAssetClassBranch = ModelChangeAssetClassBranchInput;

export type ModelChangeModelDetails = ModelChangeModelDetailsInput;

export type ModelChangeAssetClassThreshold = TargetAllocationInput;

export type ModelChangeProposal = {
  id: string;
  /** The household's own copy of the model, not the shelf model it came from. */
  financialProductId: string;
  state: ModelChangeProposalState;
  objectType?: BoundObjectType.Household;
  objectId?: string;
  targetStrategy?: Strategy;
  currentAllocations?: ModelChangeAllocation[];
  proposedAllocations?: ModelChangeAllocation[];
  currentModelDetails?: ModelChangeModelDetails;
  proposedModelDetails?: ModelChangeModelDetails;
  currentAssetClassThresholds?: ModelChangeAssetClassThreshold[];
  proposedAssetClassThresholds?: ModelChangeAssetClassThreshold[];
  currentAssetClassBranches?: ModelChangeAssetClassBranch[];
  proposedAssetClassBranches?: ModelChangeAssetClassBranch[];
};
